"use client";

import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { ArrowRight, Check, Loader2, Plus, ShieldCheck } from "lucide-react";

import {
  GlassDialog,
  GlassDialogContent,
} from "@/app/_components/marketing/glass-dialog";
import { Form, FormField } from "@/app/_components/ui/form";
import { Input } from "@/app/_components/ui/input";
import {
  planSelectionSchema,
  type PlanSelectionSchema,
} from "@/app/_lib/schemas/core-service";
import type { CoreServiceWithPlans } from "@/app/_lib/db/core-service";
import { formatPrice } from "@/app/_lib/utils";
import { cn } from "@/app/_lib/utils";
import { submitServiceEnquiry } from "../lib/actions";
import { FormError } from "@/app/_components/form-error";
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from "@/app/_components/turnstile-widget";

const PRICING_DISCLAIMER =
  "Any price displayed represents the starting investment for each plan and is based on the scope, features, and deliverables outlined within that package. If your project requires additional functionality, custom integrations, expanded features, accelerated timelines, or any work beyond the included plan benefits, the final investment may be adjusted accordingly. Before any additional work begins, we'll review your requirements, discuss any changes to the project scope, and provide a clear, transparent quote for your approval.";

const cloud = (id: string) =>
  `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${id}`;

type Feature = { key: string; value: string };

export interface ServicePromo {
  title: string;
  description?: string;
  image?: string;
}

interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  service?: CoreServiceWithPlans | null;
  /** A plan explicitly selected from the pricing columns (multi-plan variant). */
  plan?: CoreServiceWithPlans["plans"][0] | null;
  /** Specialist / Free-Consultation promotion enquiry. */
  promo?: ServicePromo | null;
}

export default function ServiceModal({
  open,
  onClose,
  service,
  plan,
  promo,
}: ServiceModalProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const [showAll, setShowAll] = React.useState(false);
  const [descExpanded, setDescExpanded] = React.useState(false);
  const [error, setError] = React.useState("");

  // Bot mitigation (guest path only — logged-in submissions go through
  // startSalesFeedback, not createGuestConversation).
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const turnstileRef = React.useRef<TurnstileWidgetHandle>(null);
  const formRenderedAtRef = React.useRef(Date.now());

  const isLoggedIn = !!session?.user;

  const form = useForm<PlanSelectionSchema>({
    resolver: zodResolver(planSelectionSchema),
    defaultValues: { firstName: "", lastName: "", email: "" },
  });

  // Prefill for logged-in users.
  React.useEffect(() => {
    if (open && isLoggedIn) {
      const u = session!.user as {
        firstname?: string;
        lastname?: string;
        email?: string;
      };
      form.reset({
        firstName: u.firstname ?? "",
        lastName: u.lastname ?? "",
        email: u.email ?? "",
      });
    }
  }, [open, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // The dialog stays mounted between opens — refresh the render timestamp
  // (timing check) and get a fresh Turnstile token each time it opens.
  React.useEffect(() => {
    if (open) {
      formRenderedAtRef.current = Date.now();
      setHoneypot("");
      turnstileRef.current?.reset();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setShowAll(false);
      setError("");
      form.reset({ firstName: "", lastName: "", email: "" });
    }, 250);
  };

  const onSubmit = async (data: PlanSelectionSchema) => {
    setError("");
    const result = await submitServiceEnquiry(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        serviceId: service?.id,
        planId: plan?.id,
        serviceName: service?.title ?? "",
        planName: plan?.name,
        promoTitle: promo?.title,
        turnstileToken,
        honeypot,
        formRenderedAt: formRenderedAtRef.current,
      },
      typeof window !== "undefined" ? window.location.origin : "",
    );

    if (!result.success || !result.feedbackId) {
      setError(
        (result as { message?: string }).message ||
          "Something went wrong. Please try again.",
      );
      turnstileRef.current?.reset();
      return;
    }

    const destination = `/dashboard/messages/${result.feedbackId}`;

    if (result.loggedIn) {
      router.push(destination);
      router.refresh();
      return;
    }

    const token = (result as { autoLoginToken?: string }).autoLoginToken;
    if (token) {
      const res = await signIn("guest-token", { token, redirect: false });
      if (res?.ok) {
        if (typeof window !== "undefined")
          localStorage.setItem("needsProfileSetup", "true");
        router.push(destination);
        router.refresh();
        return;
      }
    }

    if ((result as { emailExists?: boolean }).emailExists) {
      setError("An account with this email exists. Please sign in first.");
      return;
    }
    // New user without an immediate session — the set-password email is sent.
    setError("");
    router.push(destination);
    router.refresh();
  };

  if (!service && !promo) return null;

  // ─── Left column content ───────────────────────────────────────────────────
  const activePlan = plan ?? service?.plans?.[0] ?? null;
  const features: Feature[] = Array.isArray(activePlan?.features)
    ? (activePlan!.features as Feature[])
    : [];
  const hasBenefits = features.length > 0;
  const limit = 5;
  const shown = showAll ? features : features.slice(0, limit);
  const eyebrow = ( service?.title ? service?.title : promo?.title? "Promo" : "" ).toUpperCase();

  const priceNode =
    plan &&
    formatPrice(
      plan.minPrice,
      plan.currency,
      plan.maxPrice ?? undefined,
      plan.priceType as "FIXED" | "RANGE",
    );

  const leftColumn = (
    <div className="flex flex-col gap-5 p-8 md:w-1/2">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        {eyebrow}
      </p>

      {plan ? (
        <h3 className="text-2xl font-semibold tracking-tight">
          {plan.name}{" "}
          <span className="font-serif text-primary">{priceNode}</span>
        </h3>
      ) : (
        <h3 className="text-lg leading-snug">
          <span className="font-semibold">
            {" "}
            {promo?.title ?? `${service?.title}.`}
          </span>{" "}
          <span className="text-muted-foreground">{service?.description}</span>
        </h3>
      )}

      {hasBenefits ? (
        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Included
          </p>
          {shown.map((f, i) => (
            <div key={f.key + i} className="flex items-start gap-2.5">
              <Check size={15} className="mt-0.5 shrink-0 text-primary" />
              <p className="text-sm leading-snug text-muted-foreground">
                <span className="font-semibold text-foreground">{f.key}</span>{" "}
                {f.value}
              </p>
            </div>
          ))}
          {features.length > limit && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="flex items-center gap-1.5 pl-6 text-[11px] font-bold uppercase tracking-wider text-primary hover:opacity-70"
            >
              <Plus size={12} /> View {features.length - limit} more benefits
            </button>
          )}
        </div>
      ) : (
        <>
          <p
            className={cn(
              "text-sm leading-relaxed text-muted-foreground",
              !descExpanded && "line-clamp-3",
            )}
          >
            {promo?.description ?? service?.plans?.[0]?.description ?? service?.description}
          </p>
          {(promo?.description ?? service?.plans?.[0]?.description ?? service?.description ?? "")
            .length > 160 && (
            <button
              type="button"
              onClick={() => setDescExpanded((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary hover:opacity-70"
            >
              {descExpanded ? "See less" : "See more"}
            </button>
          )}
        </>
      )}

      <div className="mt-auto flex items-center gap-2 pt-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        <ShieldCheck size={14} /> Securely processed
      </div>
    </div>
  );

  // ─── Right column: single-step details form ────────────────────────────────
  const rightColumn = (
    <div className="border-t border-border flex flex-col gap-6 p-8 md:w-1/2 md:border-l md:border-t-0">
      <div>
        <h2 className="text-lg font-semibold">Your details</h2>
        <div className="mt-2  h-1.5 w-10 rounded-full bg-primary" />
      </div>

      {(promo?.image ?? service?.image) && (
        <div className="relative mt-1 h-40 overflow-hidden rounded-2xl bg-mint">
          <Image
            src={cloud((promo?.image ?? service?.image)!)}
            alt={service?.title ?? promo?.title ?? ""}
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
            className="object-contain transition-transform object-center duration-700 group-hover:scale-105"
          />
        </div>
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <FormField
              name="firstName"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-1">
                  <Input
                    {...field}
                    placeholder="First name"
                    className="h-12 rounded-xl bg-gray-50"
                    style={{ fontSize: "16px" }}
                  />
                  {fieldState.error && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <FormField
              name="lastName"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-1">
                  <Input
                    {...field}
                    placeholder="Last name"
                    className="h-12 rounded-xl bg-gray-50"
                    style={{ fontSize: "16px" }}
                  />
                  {fieldState.error && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <FormField
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1">
                <Input
                  {...field}
                  type="email"
                  placeholder="Email address"
                  className="h-12 rounded-xl bg-gray-50"
                  style={{ fontSize: "16px" }}
                />
                {fieldState.error && (
                  <p className="ml-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          {/* Honeypot — hidden via CSS (not type="hidden") so bots that fill
              every field in the DOM still trip it. Real users never see it. */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="service-website">Website</label>
            <input
              id="service-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <TurnstileWidget
            ref={turnstileRef}
            onVerify={setTurnstileToken}
            mode="invisible"
          />

          <FormError message={error} />

          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(
              "mt-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-white transition hover:opacity-90 disabled:opacity-50",
            )}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <>
                Continue <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      </Form>
    </div>
  );

  return (
    <GlassDialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <GlassDialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none">
        <div className="overflow-hidden rounded-2xl bg-background shadow-xl">
          <div className="flex flex-col md:flex-row">
            {leftColumn}
            {rightColumn}
          </div>
          {/* Pricing disclaimer — inside the white card so it's legible and
              reachable on mobile without hunting on the transparent overlay. */}
          <p className="border-t border-border px-8 py-4 text-[10px] leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">* Pricing Disclaimer</span>{" "}
            {PRICING_DISCLAIMER}
          </p>
        </div>
      </GlassDialogContent>
    </GlassDialog>
  );
}
